import { useMutation } from '@tanstack/react-query';
import type { QuoteItemDTO } from '@setvi/shared/services/react-query';
import { refreshAllQuoteV2PricesMutation } from 'Services/Mutations/QuotesV2';
import { mapQuoteItems } from '../utils/quote-helpers';
import { calculateGrossProfitPercentage } from '../utils/pricing-helpers';
import { useQuoteCache } from './use-quote-cache';

interface RefreshUpdatedItem {
  newSell: number;
  newCost: number;
  oldSell: number;
  oldCost: number;
  sku: string;
  itemId: string;
}

interface RefreshAllPricesResponse {
  updated: RefreshUpdatedItem[];
  failed: Array<{ reason: string; sku: string; itemId: string }>;
  totals: {
    totalCost: number;
    totalSell: number;
    totalGp: number;
    itemCount: number;
  };
}

/**
 * Mark all non-freehand products as needing pricing update
 */
const markAllProductsForPricing = (items: QuoteItemDTO[]): QuoteItemDTO[] =>
  mapQuoteItems(items, product => {
    if (!product.isFreeHand) {
      return { ...product, isPricingFetching: true };
    }
    return product;
  });

/**
 * Refresh all product prices via the V2 bulk refresh endpoint.
 * Calls POST /v2/quote/:quoteId/items/refresh-all-prices
 * and applies updated sell/cost values from the response.
 */
export const useRefreshPrices = (quoteId: string) => {
  const cache = useQuoteCache(quoteId);
  const { mutationFn } = refreshAllQuoteV2PricesMutation();

  return useMutation({
    mutationFn: () => mutationFn({ quoteId }),

    onMutate: async () => {
      await cache.cancel();

      const previousItems = cache.getItems();

      cache.setItems(old => {
        if (!old) return old;
        return markAllProductsForPricing(old);
      });

      return { previousItems };
    },

    onError: (_error, _variables, context) => {
      if (context?.previousItems) {
        cache.setItems(() => context.previousItems);
      }
    },

    onSuccess: data => {
      const response = data as RefreshAllPricesResponse;

      // Build a map of updated prices for quick lookup
      const updatedMap = new Map<string, RefreshUpdatedItem>();
      if (response?.updated?.length) {
        for (const item of response.updated) {
          updatedMap.set(item.itemId, item);
        }
      }

      // Single cache update: apply new prices + clear isPricingFetching
      cache.setItems(old => {
        if (!old) return old;
        return mapQuoteItems(old, product => {
          const id = product.id || (product as any)._id;
          const updated = id ? updatedMap.get(id) : undefined;

          if (updated) {
            return {
              ...product,
              sell: updated.newSell,
              cost: updated.newCost,
              totalGrossProfit: updated.newSell - updated.newCost,
              totalGrossProfitPercentage: calculateGrossProfitPercentage(
                updated.newCost,
                updated.newSell
              ),
              isPricingFetching: false
            };
          }

          if (product.isPricingFetching) {
            return { ...product, isPricingFetching: false };
          }

          return product;
        });
      });

      if (response?.totals) {
        cache.setQuote(old =>
          old
            ? {
                ...old,
                totalSell: response.totals.totalSell,
                totalCost: response.totals.totalCost
              }
            : old
        );
      }

      // Only refetch quote (for totals), NOT items — refetching items
      // would overwrite enriched data (names, images, vendor) with
      // lightweight metadata and also overwrite the prices we just set.
      cache.refetchQuote();
    }
  });
};
